import React, { useState } from "react";
import styles from "./RefluxMonitoring.module.css";

/**
 * JSON Tree component for displaying nested objects.
 * Supports expand/collapse and renders different types with syntax highlighting.
 * @param {Object} props
 * @param {*} props.data - Data to display
 * @param {number} [props.depth=0] - Current nesting depth
 * @param {number} [props.maxDepth=10] - Maximum depth before truncating
 */
const JsonTree = ({ data, depth = 0, maxDepth = 10 }) => {
  const [expanded, setExpanded] = useState(depth < 2); // Auto-expand first 2 levels

  if (depth > maxDepth) {
    return <span className={styles.jsonTreeFunction}>{"..."}</span>;
  }

  // Handle null/undefined
  if (data === null) {
    return <span className={styles.jsonTreeNull}>null</span>;
  }
  if (data === undefined) {
    return <span className={styles.jsonTreeUndefined}>undefined</span>;
  }

  // Handle primitives
  if (typeof data === "string") {
    return <span className={styles.jsonTreeString}>"{data}"</span>;
  }
  if (typeof data === "number") {
    return <span className={styles.jsonTreeNumber}>{data}</span>;
  }
  if (typeof data === "boolean") {
    return <span className={styles.jsonTreeBoolean}>{data.toString()}</span>;
  }
  if (typeof data === "function") {
    return <span className={styles.jsonTreeFunction}>[Function]</span>;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    if (data.length === 0) {
      return <span className={styles.jsonTreeEmpty}>[]</span>;
    }
    return (
      <div className={depth > 0 ? styles.jsonTreeNode : undefined}>
        <span onClick={() => setExpanded(!expanded)} className={styles.jsonTreeToggle}>
          {expanded ? "▼" : "▶"} Array({data.length})
        </span>
        {expanded && (
          <div className={styles.jsonTreeNode}>
            {data.map((item, index) => (
              <div key={index} className={styles.jsonTreeRow}>
                <span className={styles.jsonTreeIndex}>{index}:</span>
                <JsonTree data={item} depth={depth + 1} maxDepth={maxDepth} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Handle objects
  if (typeof data === "object") {
    const keys = Object.keys(data);
    if (keys.length === 0) {
      return <span className={styles.jsonTreeEmpty}>{"{}"}</span>;
    }
    return (
      <div className={depth > 0 ? styles.jsonTreeNode : undefined}>
        <span onClick={() => setExpanded(!expanded)} className={styles.jsonTreeToggle}>
          {expanded ? "▼" : "▶"} Object({keys.length} keys)
        </span>
        {expanded && (
          <div className={styles.jsonTreeNode}>
            {keys.map((key) => (
              <div key={key} className={styles.jsonTreeRow}>
                <span className={styles.jsonTreeKey}>{key}:</span>
                <JsonTree data={data[key]} depth={depth + 1} maxDepth={maxDepth} />
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Fallback
  return <span className={styles.jsonTreeFunction}>{String(data)}</span>;
};

export default JsonTree;
